import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { List } from 'immutable';
import { $getNodeByKey } from 'lexical';
import { getTextStylesFromBrandStyle } from 'modules/BrandDefinition';
import { CustomParagraphNode } from 'modules/Lexical/nodes/CustomParagraphNode';
import { useEffect } from 'react';
import { HeadingTagType } from 'const';
import { BrandStyleMap } from 'models';
import { registerCommands } from './commands';
import { $applyBrandHeadingTag, $getHeadingTag, $replaceParagraphsWithHeadingTags, isValidHeadingTag } from './utils';

type Props = {
  brandStyleChanged?: boolean;
  brandStyle: BrandStyleMap | undefined;
  onChange: (value: HeadingTagType | undefined) => void;
};

export function HeadingTagPlugin(props: Props): null {
  const { onChange, brandStyle, brandStyleChanged } = props;
  const [editor] = useLexicalComposerContext();

  useEffect(() => registerCommands({ editor }),
    [editor],
  );

  useEffect(() => {
    if (brandStyleChanged) {
      return;
    }
    editor.update(() => {
      $applyBrandHeadingTag(brandStyle);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, brandStyle]);

  useEffect(() => {
    const $readAndNotifyChange = (): void => {
      onChange($getHeadingTag());
    };

    editor.getEditorState().read($readAndNotifyChange);

    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read($readAndNotifyChange);
    });
  }, [editor, onChange]);

  useEffect(() => {
    // When the last node is deleted, Lexical can automatically create a
    // paragraph node. If a brand style with a heading tag is set, that
    // paragraph should not remain as a plain paragraph — we manually
    // watch for these mutations and convert such paragraphs into the
    // configured heading tag.
    const { lineHeight, fontSize } = getTextStylesFromBrandStyle(brandStyle, List([]), List([]));
    const headingTag = brandStyle?.get('tag');
    if (!isValidHeadingTag(headingTag)) {
      return;
    }

    if (!editor.hasNode(CustomParagraphNode)) {
      return;
    }

    return editor.registerMutationListener(
      CustomParagraphNode,
      (mutatedNodes) => {
        editor.update(() => {
          const uniqueParagraphs = new Set<CustomParagraphNode>();
          for (const [nodeKey] of mutatedNodes) {
            const node = $getNodeByKey<CustomParagraphNode>(nodeKey);
            if (!node) {
              continue;
            }
            uniqueParagraphs.add(node);
          }
          $replaceParagraphsWithHeadingTags({ headingTag, lineHeight, fontSize, paragraphs: uniqueParagraphs });
        });
      },
    );
  }, [editor, brandStyle]);

  return null;
}
